import { useEffect } from 'react';
import { ClerkProvider, SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';
import { Panel } from '@bcecloud/portal-ui';
import { setTokenProvider } from '@/lib/api';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { AdminSignInScreen } from '@/components/AdminSignInScreen';
import { ClerkTicketSignIn } from '@/components/ClerkTicketSignIn';
import { getE2EToken } from '@/lib/e2e-session';

/**
 * Registers Clerk's token getter with the API client.
 *
 * @returns Nothing to render.
 */
function TokenBridge(): null {
  const { getToken } = useAuth();

  useEffect(() => {
    setTokenProvider(() => getToken());
  }, [getToken]);

  return null;
}

/** TEST-ONLY: sends the test token instead of Clerk's (lib/e2e-session.ts). */
function TestTokenBridge({ token }: { readonly token: string }): null {
  useEffect(() => {
    setTokenProvider(() => Promise.resolve(token));
  }, [token]);
  return null;
}

export interface AdminAuthProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Authentication for the admin tool, against the **BCE team's own Clerk
 * instance** — not the tenant one.
 *
 * The separation is the security boundary: client users and BCE staff never
 * share a user pool, so a client's session token cannot even be *verified* on
 * staff routes, let alone authorised. Signing in here is necessary but not
 * sufficient — the API additionally requires the admin role in the BCE Clerk
 * organization, and refuses ordinary members.
 *
 * Sign-in itself is {@link AdminSignInScreen} — the shared BCE screen, not
 * Clerk's widget — and is passwordless: one-time codes only.
 *
 * @param props - The application to render once signed in.
 * @returns The provider tree.
 */
export function AdminAuthProvider({ children }: AdminAuthProviderProps): React.ReactElement {
  const publishableKey = getRuntimeConfig('CLERK_PUBLISHABLE_KEY');

  if (publishableKey === undefined) {
    return (
      <main className="grid min-h-screen place-items-center bg-portal-bg p-6">
        <Panel className="max-w-[420px] p-6">
          <h1 className="text-portal-card-title font-bold">Sign-in is not configured</h1>
          <p className="mt-2 text-portal-small text-portal-muted">
            The admin Clerk instance is not set up for this deployment. Set
            ADMIN_CLERK_PUBLISHABLE_KEY on portal-api (or VITE_ADMIN_CLERK_PUBLISHABLE_KEY for
            local dev) and reload.
          </p>
        </Panel>
      </main>
    );
  }

  // TEST-ONLY sign-in (dev builds with VITE_E2E_AUTH=1 and a #e2eToken= link):
  // skip the Clerk gate and send the test token; the local API decides who it is.
  // Clerk stays mounted so the shell's sign-out menu still renders.
  const testToken = getE2EToken();
  if (testToken !== null) {
    return (
      <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/">
        <TestTokenBridge token={testToken} />
        {children}
      </ClerkProvider>
    );
  }

  return (
    <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/">
      <TokenBridge />
      {/* A Salesforce one-time link lands with a sign-in ticket: redeem it before the gate. */}
      <ClerkTicketSignIn>
        <SignedOut>
          <AdminSignInScreen />
        </SignedOut>
        <SignedIn>{children}</SignedIn>
      </ClerkTicketSignIn>
    </ClerkProvider>
  );
}
